import { useState } from 'react';

// Module 1, 2, 4 — Lead Capture + API
// Module 12, 13, 14 — Welcome messages + Templates
// Module 15 — Response time goal
// Module 19 — Follow-up reminder

const inp = { width: '100%', padding: '8px 12px', border: '1.5px solid #e0e0e0', borderRadius: 8, fontSize: 13, fontFamily: 'inherit', boxSizing: 'border-box', outline: 'none', marginBottom: 10 };

function SectionCard({ title, sub, children }) {
  return (
    <div style={{ background: '#fff', borderRadius: 14, border: '1px solid #eef0f2', padding: '18px 22px', marginBottom: 16, boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
      <div style={{ fontSize: 13, fontWeight: 800, color: '#1a1a2e', marginBottom: 4 }}>{title}</div>
      {sub && <div style={{ fontSize: 11, color: '#aaa', marginBottom: 14 }}>{sub}</div>}
      {children}
    </div>
  );
}

function Toggle({ value, onChange }) {
  return (
    <div onClick={() => onChange(!value)} style={{
      width: 44, height: 24, borderRadius: 12, cursor: 'pointer', position: 'relative', flexShrink: 0,
      background: value ? '#25d366' : '#ccc', transition: 'background 0.2s',
    }}>
      <div style={{ position: 'absolute', top: 2, left: value ? 22 : 2, width: 20, height: 20, borderRadius: '50%', background: '#fff', transition: 'left 0.2s' }} />
    </div>
  );
}

function InfoRow({ icon, label, value, tag }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderBottom: '1px solid #f8f8f8' }}>
      <div style={{ fontSize: 12, color: '#555' }}>{icon} {label}</div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        {value && <div style={{ fontSize: 12, color: '#888', fontFamily: 'monospace' }}>{value}</div>}
        {tag && <span style={{ fontSize: 10, padding: '2px 8px', borderRadius: 10, background: tag === 'Live' ? '#e8f5e9' : tag === 'Preview' ? '#fff8e1' : '#f3e5f5', color: tag === 'Live' ? '#2e7d32' : tag === 'Preview' ? '#e65100' : '#6a1b9a', fontWeight: 700 }}>{tag}</span>}
      </div>
    </div>
  );
}

export function LeadIntegrations() {
  const [slaMinutes, setSlaMinutes] = useState(2);
  const [slaEnabled, setSlaEnabled] = useState(true);
  const [followupEnabled, setFollowupEnabled] = useState(true);
  const [followupHour, setFollowupHour] = useState('09:00');
  const [welcomeWA, setWelcomeWA] = useState(true);
  const [welcomeEmail, setWelcomeEmail] = useState(false);
  const [waTemplate, setWaTemplate] = useState('Hi {name}! Thank you for your interest in {business}. Our team will contact you shortly. 🙏');
  const [emailTemplate, setEmailTemplate] = useState('Dear {name},\n\nThank you for reaching out to us. We will get back to you within 24 hours.\n\nBest regards,\nTeam');
  const [toast, setToast] = useState('');
  const [activeTab, setActiveTab] = useState('capture');

  const flash = (msg) => { setToast(msg); setTimeout(() => setToast(''), 2000); };

  const tabs = [
    { id: 'capture', label: '🌐 Lead Capture' },
    { id: 'response', label: '⏱ Response Time' },
    { id: 'welcome', label: '👋 Welcome Msg' },
    { id: 'templates', label: '📝 Templates' },
    { id: 'reminder', label: '🔔 Reminders' },
    { id: 'api', label: '🔗 API' },
  ];

  const tabStyle = (active) => ({
    padding: '8px 16px', fontSize: 11.5, fontWeight: 700, border: 'none', borderRadius: 20, cursor: 'pointer', fontFamily: 'inherit', transition: 'all .15s',
    background: active ? '#075e54' : '#f0f0f0', color: active ? '#fff' : '#555',
  });

  return (
    <div style={{ padding: 20 }}>
      <div style={{ fontSize: 19, fontWeight: 800, color: '#1a1a2e', marginBottom: 18, letterSpacing: '-0.01em' }}>🔗 Integrations & Automation</div>

      <div style={{ display: 'flex', gap: 6, marginBottom: 20, flexWrap: 'wrap' }}>
        {tabs.map(t => <button key={t.id} style={tabStyle(activeTab === t.id)} onClick={() => setActiveTab(t.id)}>{t.label}</button>)}
      </div>

      {/* Module 1, 2 — Lead Capture */}
      {activeTab === 'capture' && (
        <div>
          <SectionCard title="🌐 Website Form Capture" sub="Leads from website form captured automatically via API">
            <InfoRow icon="🔗" label="Webhook URL" value="Not connected yet" tag="Preview" />
            <InfoRow icon="📋" label="Form ID" value="Not generated yet" tag="Preview" />
            <InfoRow icon="✅" label="Auto-assign on capture" tag="Preview" />
            <div style={{ marginTop: 12, padding: '10px 14px', background: '#fff8e1', borderRadius: 8, fontSize: 12, color: '#e65100' }}>
              ⚠️ Preview only. Website form webhook is not connected yet (backend required)
            </div>
          </SectionCard>

          <SectionCard title="📱 Multi-source Capture" sub="Facebook Ads, Google Ads, online portals">
            <InfoRow icon="📘" label="Facebook Lead Ads" tag="Preview" />
            <InfoRow icon="🔍" label="Google Ads" tag="Preview" />
            <InfoRow icon="🏪" label="IndiaMART / JustDial" tag="Preview" />
            <div style={{ marginTop: 12, padding: '10px 14px', background: '#fff8e1', borderRadius: 8, fontSize: 12, color: '#e65100' }}>
              ⚠️ Requires backend webhook + Meta App Review for Facebook Ads
            </div>
          </SectionCard>
        </div>
      )}

      {/* Module 15 — Response Time */}
      {activeTab === 'response' && (
        <SectionCard title="⏱ Response Time Goal" sub="Contact the lead within set time — warning when exceeded">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <div style={{ fontSize: 13, fontWeight: 700 }}>Enable Response Time Warning</div>
            <Toggle value={slaEnabled} onChange={setSlaEnabled} />
          </div>
          <label style={{ fontSize: 11, fontWeight: 700, color: '#555' }}>Response Time Goal (minutes)</label>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 6, marginBottom: 16 }}>
            <input type="number" style={{ ...inp, width: 120, marginBottom: 0 }} value={slaMinutes} onChange={e => setSlaMinutes(+e.target.value)} min={1} max={60} />
            <span style={{ fontSize: 12, color: '#888' }}>minutes after lead arrives</span>
          </div>
          <div style={{ padding: '12px 16px', background: slaEnabled ? '#e8f5e9' : '#f5f5f5', borderRadius: 10, fontSize: 12, color: slaEnabled ? '#2e7d32' : '#aaa' }}>
            {slaEnabled ? `✅ Warning shows if lead not contacted within ${slaMinutes} min` : '⏸ Response time warning disabled'}
          </div>
          <button onClick={() => flash('✅ Saved (preview mode — backend required for live SLA)')} style={{ marginTop: 14, padding: '9px 20px', background: '#25d366', color: '#fff', border: 'none', borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>
            Save Settings
          </button>
          {toast && <div style={{ marginTop: 10, fontSize: 12, color: '#25d366', fontWeight: 600 }}>{toast}</div>}
        </SectionCard>
      )}

      {/* Module 12, 13 — Welcome Messages */}
      {activeTab === 'welcome' && (
        <div>
          <SectionCard title="💬 Auto Welcome WhatsApp" sub="Sent to customer automatically when lead arrives">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <div style={{ fontSize: 13, fontWeight: 700 }}>Enable Auto Welcome WhatsApp</div>
              <Toggle value={welcomeWA} onChange={setWelcomeWA} />
            </div>
            <label style={{ fontSize: 11, fontWeight: 700, color: '#555' }}>Message Template</label>
            <textarea style={{ ...inp, marginTop: 6, minHeight: 80 }} value={waTemplate} onChange={e => setWaTemplate(e.target.value)} disabled={!welcomeWA} />
            <div style={{ fontSize: 11, color: '#aaa', marginBottom: 12 }}>Variables: {'{name}'}, {'{business}'}, {'{product}'}</div>
            <button onClick={() => flash('✅ Saved (preview mode — backend integration pending)')} disabled={!welcomeWA} style={{ padding: '9px 20px', background: welcomeWA ? '#25d366' : '#ccc', color: '#fff', border: 'none', borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: welcomeWA ? 'pointer' : 'not-allowed', fontFamily: 'inherit' }}>Save</button>
          </SectionCard>

          <SectionCard title="📧 Auto Welcome Email" sub="Sent to customer email automatically">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <div style={{ fontSize: 13, fontWeight: 700 }}>Enable Auto Welcome Email</div>
              <Toggle value={welcomeEmail} onChange={setWelcomeEmail} />
            </div>
            <label style={{ fontSize: 11, fontWeight: 700, color: '#555' }}>Email Template</label>
            <textarea style={{ ...inp, marginTop: 6, minHeight: 100 }} value={emailTemplate} onChange={e => setEmailTemplate(e.target.value)} disabled={!welcomeEmail} />
            <button onClick={() => flash('✅ Saved (preview mode — email backend integration pending)')} disabled={!welcomeEmail} style={{ padding: '9px 20px', background: welcomeEmail ? '#25d366' : '#ccc', color: '#fff', border: 'none', borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: welcomeEmail ? 'pointer' : 'not-allowed', fontFamily: 'inherit' }}>Save</button>
          </SectionCard>
          {toast && <div style={{ fontSize: 12, color: '#25d366', fontWeight: 600 }}>{toast}</div>}
        </div>
      )}

      {/* Module 14 — Templates */}
      {activeTab === 'templates' && (
        <SectionCard title="📝 Message Templates" sub="Customize WhatsApp and email templates">
          {[
            { name: 'Follow-up WhatsApp', type: 'whatsapp', text: 'Hi {name}, following up on your enquiry. Are you still interested in {product}?' },
            { name: 'Appointment Confirmation', type: 'whatsapp', text: 'Hi {name}, your appointment is confirmed for {date} at {time}.' },
            { name: 'Converted Thank You', type: 'whatsapp', text: 'Dear {name}, thank you for your order! We will process it shortly.' },
            { name: 'Lost — Re-engage', type: 'email', text: 'Dear {name}, we noticed you were interested in {product}. Special offer available now!' },
          ].map((t, i) => (
            <div key={i} style={{ padding: '12px 0', borderBottom: '1px solid #f8f8f8' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: '#333' }}>{t.name}</div>
                <span style={{ fontSize: 10, padding: '2px 8px', borderRadius: 10, background: t.type === 'whatsapp' ? '#e8f5e9' : '#e3f2fd', color: t.type === 'whatsapp' ? '#2e7d32' : '#1565c0', fontWeight: 700 }}>
                  {t.type === 'whatsapp' ? '💬 WA' : '📧 Email'}
                </span>
              </div>
              <div style={{ fontSize: 11, color: '#888', lineHeight: 1.5 }}>{t.text}</div>
            </div>
          ))}
          <button onClick={() => flash('✅ Templates saved (preview mode)')} style={{ marginTop: 14, padding: '9px 20px', background: '#25d366', color: '#fff', border: 'none', borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>Save Templates</button>
          {toast && <div style={{ marginTop: 10, fontSize: 12, color: '#25d366', fontWeight: 600 }}>{toast}</div>}
        </SectionCard>
      )}

      {/* Module 19 — Follow-up Reminder */}
      {activeTab === 'reminder' && (
        <SectionCard title="🔔 Follow-up Reminder" sub="Agent notified on follow-up date">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <div style={{ fontSize: 13, fontWeight: 700 }}>Enable Follow-up Reminders</div>
            <Toggle value={followupEnabled} onChange={setFollowupEnabled} />
          </div>
          <label style={{ fontSize: 11, fontWeight: 700, color: '#555' }}>Reminder Time</label>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 6, marginBottom: 16 }}>
            <input type="time" style={{ ...inp, width: 140, marginBottom: 0 }} value={followupHour} onChange={e => setFollowupHour(e.target.value)} />
            <span style={{ fontSize: 12, color: '#888' }}>on the follow-up date</span>
          </div>
          <div style={{ padding: '12px 16px', background: '#f0faf5', borderRadius: 10, fontSize: 12, color: '#075e54', marginBottom: 14 }}>
            📋 How it works: Agent sets follow-up date via Quick Action → on that date at {followupHour}, agent gets notified → lead appears in Daily View "Today's Follow-ups" bucket
          </div>
          <div style={{ padding: '10px 14px', background: '#fff8e1', borderRadius: 8, fontSize: 11, color: '#e65100' }}>
            ⚠️ Daily View bucket is live. Push notifications require backend cron + device token.
          </div>
          <button onClick={() => flash('✅ Saved (preview mode)')} style={{ marginTop: 14, padding: '9px 20px', background: '#25d366', color: '#fff', border: 'none', borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>Save</button>
          {toast && <div style={{ marginTop: 10, fontSize: 12, color: '#25d366', fontWeight: 600 }}>{toast}</div>}
        </SectionCard>
      )}

      {/* Module 4 — API */}
      {activeTab === 'api' && (
        <SectionCard title="🔗 Incoming & Outgoing API" sub="Receive leads from other systems and send lead data out">
          <InfoRow icon="📥" label="Incoming Lead API" value="POST /api/leads" tag="Live" />
          <InfoRow icon="📤" label="Outgoing Webhook" value="Configure URL below" tag="Preview" />
          <InfoRow icon="🔑" label="API Key" value="Not generated yet" tag="Preview" />
          <div style={{ marginTop: 14 }}>
            <label style={{ fontSize: 11, fontWeight: 700, color: '#555' }}>Outgoing Webhook URL</label>
            <input style={{ ...inp, marginTop: 6 }} placeholder="https://your-crm.com/webhook" />
            <label style={{ fontSize: 11, fontWeight: 700, color: '#555' }}>Events to send</label>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 8, marginBottom: 14 }}>
              {['New Lead', 'Status Change', 'Converted', 'Assigned'].map(e => (
                <label key={e} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, cursor: 'pointer' }}>
                  <input type="checkbox" defaultChecked /> {e}
                </label>
              ))}
            </div>
            <button onClick={() => flash('✅ Saved (preview mode — backend integration pending)')} style={{ padding: '9px 20px', background: '#25d366', color: '#fff', border: 'none', borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>Save Webhook</button>
          </div>
          {toast && <div style={{ marginTop: 10, fontSize: 12, color: '#25d366', fontWeight: 600 }}>{toast}</div>}
        </SectionCard>
      )}
    </div>
  );
}
